import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FilterModel } from 'utils/globalModels';
import { FilterLogicsType, FilterOperatorsType } from 'constants/filters';
import { parseFiltersToBeSent } from 'utils/filter';
import { TableFilterModel, TableFilterOptionModel } from '../models';

export interface TableFilterChangeModel {
  option: TableFilterOptionModel;
  value?: string;
}

export interface TableFilterErrorOptionModel {
  option: TableFilterOptionModel;
  relatedOption: TableFilterOptionModel;
  operator: FilterOperatorsType;
  errorMessage: string;
}

interface Props {
  initialFilters: TableFilterModel[];
  onApply: (url: string, newFilters?: FilterModel) => void;
  isMultiple: boolean;
}

export const useFilters = ({ initialFilters, onApply, isMultiple }: Props) => {
  const { t } = useTranslation();
  const [filters, setFilters] = useState<TableFilterModel[]>([]);
  const [filterOptions, setFilterOptions] = useState<TableFilterOptionModel[]>([]);
  const [isVisible, setIsVisible] = useState(false);
  const [isApplyButtonDisabled, setIsApplyButtonDisabled] = useState<boolean>(false);

  const numberOfFiltersApplied = filterOptions.filter(({ selected }) => selected).length;
  const resetButtonDisabled = numberOfFiltersApplied === 0;

  const literals: Record<string, string> = {
    headerButtonText: t('_FILTER_HEADER_BUTTON_TEXT'),
    footerCloseButtonText: t('close'),
    footerMainButtonText: t('_FILTER_FOOTER_MAIN_BUTTON_TEXT'),
    footerSecondaryButtonText: t('_FILTER_FOOTER_SECONDARY_BUTTON_TEXT'),
    sliderInitialValueLabel: t('_FROM'),
    sliderFinalValueLabel: t('_UNTIL'),
  };

  const validateCheckboxOption = (option: TableFilterOptionModel) => {
    return filterOptions.map((filterOption) => {
      const isSelectedOption = filterOption.key === option.key;
      const parsedFilterOption = isMultiple ? filterOption : { ...filterOption, selected: false };

      return isSelectedOption ? option : parsedFilterOption;
    });
  };

  const onChangeFilter = (option: TableFilterOptionModel) => {
    const validatedFilters = validateCheckboxOption(option);
    setFilterOptions(validatedFilters);
  };

  const getUrlFilters = (selectedFilters: TableFilterOptionModel[]): string => {
    return selectedFilters.map(({ key, operator, value }) => `${key.split(':')[0]}${operator}${value}`).join(FilterLogicsType.OR);
  };

  const sendFilters = () => {
    const selectedFilterOptions = filterOptions.filter(({ selected }) => selected);
    const url = getUrlFilters(selectedFilterOptions);
    const parsedFilters = parseFiltersToBeSent(url);
    onApply(url, parsedFilters);
  };

  const onApplyFilters = () => {
    setIsVisible(false);
    sendFilters();
  };

  const onResetFilters = () => {
    const parsedFilters = filterOptions.map((filterOption) => ({
      ...filterOption,
      selected: false,
      value: '',
      error: false,
      errorMessage: '',
    }));
    setFilterOptions(parsedFilters);
  };

  const onCloseFilters = () => {
    setIsVisible(false);
  };

  const onClickFiltersMenu = () => {
    setIsVisible(!isVisible);
  };

  const getData = (defaultInitialFilters: TableFilterModel[]) => {
    const initialFilteredOptions = defaultInitialFilters.flatMap((initialFilter) => initialFilter.options);
    setFilterOptions(initialFilteredOptions);
    setFilters(defaultInitialFilters);
  };

  useEffect(() => {
    !!initialFilters.length && getData(initialFilters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialFilters]);

  useEffect(() => {
    const hasErrors = filterOptions.some((checkedFilter) => !!checkedFilter.error);
    setIsApplyButtonDisabled(hasErrors);
  }, [filterOptions]);

  return {
    filters,
    filterOptions,
    numberOfFiltersApplied,
    literals,
    onClickFiltersMenu,
    onChangeFilter,
    onApplyFilters,
    onResetFilters,
    onCloseFilters,
    resetButtonDisabled,
    visible: isVisible,
    applyButtonDisabled: isApplyButtonDisabled,
  };
};
